import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import RecaptchaV2, { resetRecaptcha } from '../components/RecaptchaV2';
import { useAuthConfig } from '../hooks/useAuthConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ResetPassword = () => {
  const { recaptchaSiteKey, recaptchaEnabled, loading: configLoading } = useAuthConfig();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => String(searchParams.get('token') || '').trim(), [searchParams]);
  const emailFromLink = useMemo(
    () => String(searchParams.get('email') || '').trim(),
    [searchParams]
  );

  const [email, setEmail] = useState(emailFromLink);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/reset-password`, {
        email,
        token,
        password,
        recaptchaToken,
      });
      setMessage(res.data?.message || 'Password updated successfully.');
      setRecaptchaToken(null);
      resetRecaptcha();
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
      setRecaptchaToken(null);
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-login bg-white">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-8">
            <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mb-6" />
            <h2 className="text-[30px] font-semibold text-ink">Reset password</h2>
            <p className="text-[16px] text-[#475467] mt-1">Choose a new password for your account.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-chip mb-5 text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-100 text-green-700 px-4 py-3 rounded-chip mb-5 text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">New password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted pr-16"
                  placeholder="Min. 6 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-ink-muted hover:text-primary"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-label mb-1.5">Confirm password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-border-soft rounded-chip shadow-input focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-[16px] placeholder:text-ink-muted"
                placeholder="Re-enter password"
                required
              />
            </div>

            {!configLoading && (
              <RecaptchaV2 siteKey={recaptchaSiteKey} onChange={setRecaptchaToken} />
            )}

            <button
              type="submit"
              disabled={loading || configLoading || !token}
              className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 px-[18px] rounded-chip shadow-input transition-all text-[16px] disabled:opacity-50"
            >
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-ink-muted">
            <Link to="/login" className="text-primary hover:text-primary-hover font-semibold">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
